import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import type { FormikErrors, FormikValues } from 'formik';
import { FormGroup, FormText, InputGroup } from 'reactstrap';
import ErrorMessage from 'atom-components/form-fields-for-formik/error-message/error-message';
import { useFastField } from 'atom-components/form-fields-for-formik/field/hooks';
import type { FieldProps } from 'atom-components/form-fields-for-formik/types';
import Label from 'atom-components/label';
import type {
  ActionMeta,
  GroupBase,
  MultiValue,
  OnChangeValue,
  OptionsOrGroups,
  Props as ReactSelectProps,
  SingleValue
} from 'atom-components/select/select';
import Select from 'atom-components/select/select';

type CommonFieldProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<ReactSelectProps<Option, IsMulti, Group>, 'value'> & {
  isOnlyValue?: boolean;
};

export type SelectProps<
  Option,
  IsMulti extends boolean | undefined = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = IsMulti extends true
  ? CommonFieldProps<Option, true, Group> & {
      limit?: number;
      allOption?: Partial<Option>;
      isMulti: IsMulti;
      value: never[] | string[] | MultiValue<Option>;
      setValue(v: never[] | string[] | MultiValue<Option>): void;
    }
  : CommonFieldProps<Option, false, Group> & {
      limit?: undefined;
      allOption?: undefined;
      isMulti?: IsMulti;
      value: string | SingleValue<Option>;
      setValue(v: string | SingleValue<Option>): void;
    };

export function SingleSelectField<Option, Group extends GroupBase<Option> = GroupBase<Option>>({
  name,
  options,
  getOptionValue = (o): string => (o as unknown as { value: string }).value,
  isOnlyValue,
  className,
  onChange: externalOnChange,
  value,
  setValue,
  ...restProps
}: SelectProps<Option, false, Group>): JSX.Element {
  const onChange = useCallback(
    (option: OnChangeValue<Option, false>, actionMeta: ActionMeta<Option>) => {
      if (externalOnChange) {
        externalOnChange(option, actionMeta);
      }

      if (isOnlyValue) {
        setValue(option ? getOptionValue(option) : option);
      } else {
        setValue(option);
      }
    },
    [externalOnChange, getOptionValue, isOnlyValue, setValue]
  );

  const flatOptions = useMemo(
    () => options?.flatMap((o) => (o as GroupBase<Option>).options || o),
    [options]
  );

  const selectedValue = useMemo(
    () =>
      flatOptions?.find((o) => {
        if (isOnlyValue) {
          return getOptionValue(o as Option) === String(value);
        }
        return value && getOptionValue(o as Option) === getOptionValue(value as Option);
      }) as Option,
    [flatOptions, getOptionValue, isOnlyValue, value]
  );

  return (
    <Select<Option, false, Group>
      className={className}
      isSearchable
      isClearable
      options={options}
      instanceId={name}
      name={name}
      inputId={name}
      value={selectedValue}
      onChange={onChange}
      isDisabled={restProps.isDisabled}
      {...{ ...restProps, getOptionValue }}
    />
  );
}

export function MultiSelectField<Option, Group extends GroupBase<Option> = GroupBase<Option>>({
  name,
  isMulti,
  limit,
  options: providedOptions = [],
  getOptionValue = (o): string => (o as unknown as { value: string }).value,
  isOnlyValue,
  allOption,
  className,
  onChange: externalOnChange,
  value = [],
  setValue,
  ...restProps
}: SelectProps<Option, true, Group>): ReactElement {
  /* Check if we have reached the limit (if specified) */
  const limitReached = useMemo(() => limit && value.length >= limit, [limit, value]);

  const options = useMemo(
    () =>
      allOption && providedOptions?.length
        ? [allOption as Option, ...providedOptions]
        : providedOptions,
    [allOption, providedOptions]
  );

  const finalValue = useMemo(() => {
    if (allOption && (value as (string | number)[])?.length === providedOptions?.length) {
      return [getOptionValue(allOption as Option), ...(value as (string | number)[])];
    }
    return value;
  }, [allOption, getOptionValue, providedOptions?.length, value]);

  const onChange = useCallback(
    (option: OnChangeValue<Option, true>, actionMeta: ActionMeta<Option>) => {
      if (externalOnChange) {
        externalOnChange(option, actionMeta);
      }

      if (isOnlyValue) {
        setValue((option as MultiValue<Option>).map((o: Option) => getOptionValue(o)));
      } else {
        setValue(option);
      }
    },
    [externalOnChange, getOptionValue, isOnlyValue, setValue]
  );

  const onChangeSelectAll = useCallback(
    (rawOptions: OnChangeValue<Option, true>, meta: ActionMeta<Option>) => {
      const flatOptions = options?.flatMap(
        (o) => (o as GroupBase<Option>).options || o
      ) as Option[];

      if (!allOption) {
        onChange(rawOptions, meta);
      } else {
        const allValue = getOptionValue(allOption as Option);
        const selectedOptions = rawOptions as OnChangeValue<Option, true>;

        if (
          meta.action === 'select-option' &&
          selectedOptions.find((o) => getOptionValue(o) === allValue)
        ) {
          onChange(flatOptions, meta);
        } else if (
          meta.action === 'remove-value' &&
          getOptionValue(meta.removedValue) === allValue
        ) {
          onChange([], meta);
        } else if (meta.action === 'remove-value') {
          onChange(
            selectedOptions.filter((o) => getOptionValue(o) !== allValue),
            meta
          );
        } else if (selectedOptions.length === (options?.length || 0) - 1) {
          onChange(flatOptions, meta);
        } else {
          onChange(selectedOptions, meta);
        }
      }
    },
    [allOption, getOptionValue, onChange, options]
  );

  const selectedValue: Option | Option[] = useMemo(
    () =>
      isOnlyValue
        ? ((finalValue as unknown as string[])?.map((v) =>
            (options as Option[])?.find((o: Option) => getOptionValue(o) === String(v))
          ) as Option[])
        : (finalValue as Option | Option[]),
    [getOptionValue, isOnlyValue, options, finalValue]
  );

  return (
    <Select<Option, true, Group>
      className={className}
      isSearchable
      isClearable
      options={options}
      instanceId={name}
      name={name}
      inputId={name}
      value={selectedValue}
      onChange={onChangeSelectAll}
      isMulti={isMulti}
      noOptionsMessage={(): string | undefined =>
        limitReached ? `You can only select ${limit} options` : undefined
      }
      isDisabled={restProps.isDisabled}
      {...{ ...restProps, getOptionValue }}
    />
  );
}

export type SelectFieldProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<SelectProps<Option, IsMulti, Group>, 'value' | 'setValue'> &
  FieldProps & {
    label?: ReactNode;
    isRequired?: boolean;
    helperMessage?: ReactNode;
    isMulti?: IsMulti;
    options: OptionsOrGroups<Option, GroupBase<Option>> | undefined;
  };

export function SelectField<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  helperMessage,
  formGroupClassName,
  hideErrorValidation,
  hideSuccessValidation,
  label,
  isRequired,
  ...props
}: SelectFieldProps<Option, IsMulti, Group>): JSX.Element {
  const [, { value, touched, error }, { setValue, setTouched }] =
    useFastField<
      IsMulti extends true ? never[] | string[] | MultiValue<Option> : string | SingleValue<Option>
    >(name);
  const isValid = touched && !error;
  const isError = touched && !!error;

  const className = clsx(
    {
      'is-invalid': isError && !hideErrorValidation,
      'is-valid': isValid && !hideSuccessValidation,
      disabled: props.readOnly || props.isDisabled
    },
    props.className
  );

  return (
    <FormGroup
      className={clsx(
        formGroupClassName,
        hideErrorValidation && hideSuccessValidation ? 'pb-1' : 'pb-3'
      )}
    >
      {!!label && (
        <Label htmlFor={name} required={isRequired}>
          {label}
        </Label>
      )}
      <InputGroup>
        {props.isMulti ? (
          <MultiSelectField
            {...props}
            className={className}
            value={value as never[] | string[] | MultiValue<Option>}
            setValue={setValue as (v: never[] | string[] | MultiValue<Option>) => void}
            onBlur={(): Promise<void | FormikErrors<FormikValues>> => setTouched(true)}
            isDisabled={props.readOnly || props.isDisabled}
            isMulti
          />
        ) : (
          <SingleSelectField
            {...props}
            className={className}
            value={value}
            setValue={setValue}
            onBlur={(): Promise<void | FormikErrors<FormikValues>> => setTouched(true)}
            isDisabled={props.readOnly || props.isDisabled}
          />
        )}
        <ErrorMessage
          isError={isError}
          error={typeof error === 'string' ? error : String(Object.values(error || {})[0])}
        />
      </InputGroup>
      {helperMessage && <FormText>{helperMessage}</FormText>}
    </FormGroup>
  );
}
